import { N0SsrPassengerUpdateRequest } from '@models/requests/ssr-passenger-update-request.model';
import { createSelector } from '@ngrx/store';
import { selectExtrasFeatureState } from '@reducers';
import { convertBookingExtrasToSsrUpdate } from '@utils/ssrs.utils';
import {
  selectBookingDetails,
  selectBookingExtras,
  selectBookingExtrasTotalCost,
  selectBookingPaidExtrasTotalCost,
  selectBookingSsrAvailability,
  selectJourneyExtrasCanSell,
  selectJourneysCanSellExtras,
  selectNonInfantPassengers,
} from './booking.selector';
import {getSsrPriceForSummary} from "@utils/extras-utils";
import { selectStationsState } from './station.selector';
import { TripType } from '@constants/home.constants';

export const selectExtrasTotalSelectionsState = createSelector(
  selectExtrasFeatureState,
  (state) => state.totalSelections,
);

export const selectIncludedOrPaidExtrasByJourney = createSelector(
  selectBookingExtras,
  (bookingExtras) => {
    if (!bookingExtras || bookingExtras.length === 0) return null;

    let journeys: N0SsrPassengerUpdateRequest[] = [];

    bookingExtras.forEach((j) => {
      const journeyExtras = convertBookingExtrasToSsrUpdate(j);
      journeys = journeys.concat(journeyExtras);
    });

    return journeys;
  },
);

export const selectExtrasTotalPriceTripSummary = createSelector(
  selectExtrasTotalSelectionsState,
  selectBookingSsrAvailability,
  selectIncludedOrPaidExtrasByJourney,
  selectBookingExtrasTotalCost,
  selectBookingPaidExtrasTotalCost,
  (
    selections,
    ssrAvailability,
    includedOrPaidExtras,
    bookingExtrasTotalCost,
    paidExtrasTotalCost,
  ) => {
    let totalPrice = 0;

    if (!selections) {
      return bookingExtrasTotalCost;
    }

    selections.forEach((s) => {
      s.passengers.forEach((p) => {
        p.ssrs.forEach((ssr) => {
          if (ssr.count > 0) {
              const count = ssr.count -
                (includedOrPaidExtras
                  ?.find((j) => j.journeyKey === s.journeyKey)
                  ?.passengers.find(
                    (pass) => pass.passengerKey === p.passengerKey,
                  )
                  ?.ssrs.find(
                    (includedSsr) => includedSsr.ssrCode === ssr.ssrCode,
                  )?.count ?? 0);

              const price = getSsrPriceForSummary({
                selection: s,
                passenger: p,
                ssr,
                ssrAvailability,
              });

              totalPrice += count * price;
          }
        });
      });
    });

    totalPrice += paidExtrasTotalCost;

    return totalPrice;
  },
);

export const selectExtrasTotalPriceTripSummaryRounded = createSelector(
  selectExtrasTotalPriceTripSummary,
  Math.ceil,
);

export const selectExtrasWithPrices = createSelector(
  selectExtrasTotalSelectionsState,
  selectBookingSsrAvailability,
  selectIncludedOrPaidExtrasByJourney,
  (
    selections,
    ssrAvailability,
    includedOrPaidExtras,
  ) => {
    const extras: {price: number, name: string}[] = [];
    if (!selections) {
      return extras;
    }
    selections.forEach((s) => {
      s.passengers.forEach((p) => {
        p.ssrs.forEach((ssr) => {
          if (ssr.count > 0) {
            const subTotal =
              (ssr.count -
                (includedOrPaidExtras
                  ?.find((j) => j.journeyKey === s.journeyKey)
                  ?.passengers.find(
                    (pass) => pass.passengerKey === p.passengerKey,
                  )
                  ?.ssrs.find(
                    (includedSsr) => includedSsr.ssrCode === ssr.ssrCode,
                  )?.count ?? 0)) *
              (ssrAvailability?.journeySsrs
                .find((j) => j.journeyKey === s.journeyKey)
                ?.extrasSsrs.find(
                  (extrasSsr) => extrasSsr.ssrCode === ssr.ssrCode,
                )
                ?.passengersAvailability.find(
                  (pa) => pa.passengerKey === p.passengerKey,
                )?.price ?? 0);
            if (subTotal > 0) {
              extras.push({
                price: subTotal,
                name: ssr.ssrCode
              })
            }
          }
        });
      });
    });

    return extras;
  },
);

export const selectAncillaryExtrasData = createSelector(
  selectNonInfantPassengers,
  selectExtrasTotalSelectionsState,
  selectJourneyExtrasCanSell,
  selectJourneysCanSellExtras,
  selectBookingSsrAvailability,
  selectBookingDetails,
  selectStationsState,
  (
    passengers,
    totalSelectionExtras,
    journeyExtras,
    canSellExtrasJourneys,
    ssrAvailability,
    bookingDetails,
    stations
  ) => {
    const data = {
      passengers,
      totalSelectionExtras,
      journeyExtras,
      canSellExtrasJourneys,
      ssrAvailability,
      tripType: bookingDetails?.tripType as TripType ?? TripType.roundTrip,
      stations
    }
    return data;
  }
);